import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { api, formatCurrency, formatDate } from '../../utils/api'
import { postPriceWithFloorConfirm } from '../../utils/consignmentFloor'
import { useAuth } from '../../hooks/useAuth'
import StaffPickButton from '../shared/StaffPickButton'
import { PRODUCT_TYPES, SHOPIFY_ADMIN_URL } from '../../utils/constants'
import {
  EditableCell,
  EditablePriceCell,
  CategoryDropdown,
  SearchLinks,
  MetricsRow,
  StatusBadge,
  DraftStatusBadge,
  ProgressBar
} from '../shared'
import ProductDetailModal from '../shared/ProductDetailModal'
import PrintFallbackDialog from '../shared/PrintFallbackDialog'
import { useLabelPrinting } from '../../hooks/useLabelPrinting'
import { perUnitLineTotal } from './draftsHelpers'
import OffOnStaging from '../OffOnStaging'

// A draft is "missing a category" if product_type is empty OR carries a
// legacy/unmatched value that isn't in the current canonical PRODUCT_TYPES
// list. Both render as "— Select Category —" in the dropdown, so we treat
// them the same way for the red signal, the Activate hard-block, the Price
// confirm dialog, and the "No Category" filter/stat.
const isMissingCategory = (draft) =>
  !draft.product_type || !PRODUCT_TYPES.includes(draft.product_type)

// Shopify stores an unset inventory-item cost as '0', so a genuine £0 and
// "never entered" are indistinguishable — both get the red signal and the
// Activate confirm, since a £0-cost item deserves a look anyway.
const hasMissingCost = (draft) => (draft.costPrice || 0) <= 0

// Badge colour for the draft's quote stage (canonical QuoteStage values).
// Stages not listed read neutral.
const QUOTE_STAGE_BADGE = {
  in_warehouse: 'badge-info',
  closed_won: 'badge-success',
  accepted: 'badge-warning',
  collected: 'badge-warning',
  closed_lost: 'badge-error',
  archived: 'badge-error',
}

// The quick-filter chip buckets — one predicate per chip, deciding whether a
// draft belongs in that bucket. Shared by the row filter (a draft must match
// EVERY toggled bucket) and the contextual chip counts, so the two can never
// drift. 'unpaid' mirrors the row badge: a missing gate overlay reads as unpaid,
// and 'notApproved' as not approved, exactly as the badges do.
const QUICK_FILTER_BUCKETS = {
  notApproved: (d) => d.approvalStatus !== 'approved',
  unpaid: (d) => !d.gateConsignment && !d.paid,
  notPutaway: (d) => !d.locationLabel,
  noCost: (d) => hasMissingCost(d),
}

// Each quick-filter chip is a 3-way cycle: both (resting) → negative → positive
// → both. 'negative' keeps the drafts IN the bucket (the "problem" side the old
// two-state chip showed); 'positive' keeps the inverse (the resolved side).
// `both` is the resting label (no count); negative/positive render as
// "✓ <label> (count)". Keys and order match QUICK_FILTER_BUCKETS.
const QUICK_FILTER_LABELS = {
  notApproved: { both: 'Approval: both', negative: 'Not yet approved', positive: 'Approved' },
  unpaid: { both: 'Payment: both', negative: 'Unpaid', positive: 'Paid' },
  notPutaway: { both: 'Putaway: both', negative: 'Not yet putaway', positive: 'Put away' },
  noCost: { both: 'Cost: both', negative: 'No cost', positive: 'Has cost' },
}

// A label needs a title (the thing read across the aisle) and a price set —
// an unpriced draft isn't ready to be shelved under a printed label.
const canPrintLabel = (draft) => Boolean(draft.title?.trim()) && draft.sellPrice > 0

const PRINT_BUTTON_TEXT = {
  idle: '🏷 Print',
  printing: 'Printing…',
  printed: '✓ Printed',
  failed: '⚠ Retry'
}

// Per-item ceiling for "Activate now". One draft's activation touches Shopify
// and the pricing engine; a few seconds is normal. If a single request hangs
// past this, we abort it, record it as timed out, and move on — so the overlay
// always reaches a closeable state rather than trapping the admin behind a
// modal with a request that never returns.
const ACTIVATE_ITEM_TIMEOUT_MS = 60000

function DraftsManager() {
  const { user, hasPermission } = useAuth()
  const [drafts, setDrafts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selected, setSelected] = useState(new Set())
  // Live "Activate now" job — null when idle, otherwise the overlay's progress:
  // { total, done, current: {id,title}|null, activated: string[],
  //   held: [{shopify_product_id, outcome}], phase: 'running'|'done', cancelled }.
  // A ref carries the cancel flag so the running loop sees it without a re-render.
  const [activateJob, setActivateJob] = useState(null)
  const cancelActivateRef = useRef(false)
  // The in-flight item's AbortController, so Cancel (or the per-item timeout)
  // can interrupt a request that is hanging instead of wedging the overlay open.
  const inflightAbortRef = useRef(null)
  const [filter, setFilter] = useState('all')
  // Quick-filter chips below the tiles — a per-dimension mode map, ANDed with
  // each other and with the Search box + Status dropdown. Keys are
  // 'notApproved' | 'unpaid' | 'notPutaway' | 'noCost'; a present key holds
  // 'negative' (show the problem side) or 'positive' (show the resolved side).
  // An absent key is the resting 'both' state (no constraint). Cycling more
  // chips narrows the list, matching how the rest of the page's filters compose.
  const [quickFilterModes, setQuickFilterModes] = useState(() => ({}))
  const [sortBy, setSortBy] = useState('title')
  const [sortDir, setSortDir] = useState('asc')
  const [searchTerm, setSearchTerm] = useState('')
  const [pendingChanges, setPendingChanges] = useState({})
  const [saving, setSaving] = useState(false)
  const [limit, setLimit] = useState(100)
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [loadingProduct, setLoadingProduct] = useState(false)
  const [approving, setApproving] = useState(false)
  // Can this user approve drafts? (hasPermission returns true for admins.)
  const canApprove = hasPermission('listings:approve') || hasPermission('listings:approve_any')
  // Engine-computed launch price per channel, keyed by anchor (sell price) in
  // cents. Read-only preview from /api/pricing/preview — the math lives in the
  // Python engine, never duplicated here.
  const [previewMap, setPreviewMap] = useState({})
  const { rowState, printOne, printSelected, bulkPrinting, fallback, clearFallback } = useLabelPrinting()

  const fetchDrafts = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      // Fetch Shopify drafts and Prism's location data in parallel.
      // We used to show draft.sku under each title (which historically
      // encoded the warehouse location); now location is authoritative
      // in Prism's item_location table, joined by shopify_product_id.
      // If the location call fails we still render the drafts list — just
      // without the shelf label.
      const [draftsResponse, locationsResponse] = await Promise.all([
        api.get('/api/inventory/shopify/drafts'),
        api.get('/api/inventory-locations/items?status=draft&limit=200').catch(() => null)
      ])

      const locationList = Array.isArray(locationsResponse)
        ? locationsResponse
        : (locationsResponse?.items || [])
      const locationsByProduct = new Map()
      for (const item of locationList) {
        if (item.shopify_product_id) {
          locationsByProduct.set(String(item.shopify_product_id), {
            shelf_id: item.current_shelf_id || null,
            position: item.current_position,
            // Authoritative ownership fact (inventory_item.ownership) — lets the
            // Pricing column show consignment drafts as curve items. A draft lists
            // at its agreed price on day 1 (curve month 1), then glides down once live.
            ownership: item.ownership
          })
        }
      }

      const enrichedDrafts = (draftsResponse.drafts || []).map(d => {
        const loc = locationsByProduct.get(String(d.id))
        // Bay only — warehouse team asked to hide the L/R/slot position
        // from the drafts table. Position still lives in `loc.position`
        // and on item_location rows if anything downstream needs it.
        const locationLabel = loc ? loc.shelf_id : null
        return {
          ...d,
          sellPrice: parseFloat(d.price) || 0,
          costPrice: parseFloat(d.cost) || 0,
          isFlagged: parseFloat(d.price) === 0.01,
          isUnpriced: parseFloat(d.price) === 0,
          imageCount: d.imageCount || 0,
          locationLabel,
          isConsignment: loc?.ownership === 'consignment'
        }
      })
      // Overlay approval + paid gate state from Prism (via the Worker, which
      // resolves created_by from D1). Best-effort: a failure still shows drafts.
      let withApproval = enrichedDrafts
      try {
        const ids = enrichedDrafts.map(d => String(d.id))
        if (ids.length > 0) {
          const stateResp = await api.post('/api/listings/state', { productIds: ids })
          const states = stateResp?.states || {}
          withApproval = enrichedDrafts.map(d => {
            const s = states[String(d.id)]
            return s
              ? {
                  ...d,
                  approvalStatus: s.approval_status,
                  approvedBy: s.approved_by,
                  createdBy: s.created_by,
                  paid: s.paid,
                  gateConsignment: s.consignment,
                  heldReason: s.held_reason,
                  trialPrice: s.trialPrice,
                  quoteReference: s.quote_reference,
                  quoteStage: s.quote_stage,
                  quoteStageLabel: s.quote_stage_label
                }
              : d
          })
        }
      } catch (e) {
        console.error('Failed to load approval state:', e.message)
      }
      setDrafts(withApproval)
      setPendingChanges({})
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchDrafts()
  }, [fetchDrafts])

  // Distinct anchor (sell price) values on the page, in cents.
  const anchorCentsKey = useMemo(() => {
    const cents = new Set(
      drafts.map(d => Math.round((d.sellPrice || 0) * 100)).filter(c => c > 0)
    )
    return [...cents].sort((a, b) => a - b).join(',')
  }, [drafts])

  // Batch-fetch the engine's per-channel launch prices for those anchors.
  // Debounced so inline price edits don't spam the endpoint.
  useEffect(() => {
    if (!anchorCentsKey) { setPreviewMap({}); return }
    const handle = setTimeout(() => {
      api.get(`/api/pricing/preview?anchor_cents=${anchorCentsKey}`)
        .then(items => {
          const m = {}
          for (const it of items || []) m[it.anchor_cents] = it
          setPreviewMap(m)
        })
        .catch(() => {})
    }, 400)
    return () => clearTimeout(handle)
  }, [anchorCentsKey])

  // Track changes to a draft
  const updateDraft = (id, field, value) => {
    setDrafts(drafts.map(d => {
      if (d.id === id) {
        const updated = { ...d, [field]: value }
        if (field === 'price') {
          updated.sellPrice = parseFloat(value) || 0
          updated.isFlagged = updated.sellPrice === 0.01
          updated.isUnpriced = updated.sellPrice === 0
        }
        if (field === 'cost') {
          updated.costPrice = parseFloat(value) || 0
        }
        return updated
      }
      return d
    }))

    setPendingChanges(prev => ({
      ...prev,
      [id]: {
        ...(prev[id] || {}),
        [field]: value
      }
    }))
  }

  // Save pending changes to Shopify
  const saveChanges = async () => {
    const changedIds = Object.keys(pendingChanges)
    if (changedIds.length === 0) return

    setSaving(true)
    try {
      for (const id of changedIds) {
        const changes = pendingChanges[id]
        // D15: a below-floor consignment price warns-and-confirms, never blocks.
        await postPriceWithFloorConfirm(`/api/inventory/shopify/update/${id}`, changes)
      }
      setPendingChanges({})
      alert(`Saved changes to ${changedIds.length} product(s)`)
    } catch (err) {
      alert(`Error saving: ${err.message}`)
    } finally {
      setSaving(false)
    }
  }

  const toggleSelect = (id) => {
    const newSelected = new Set(selected)
    if (newSelected.has(id)) newSelected.delete(id)
    else newSelected.add(id)
    setSelected(newSelected)
  }

  const selectAllVisible = () => {
    const visibleIds = filteredDrafts.map(d => d.id)
    const allSelected = visibleIds.every(id => selected.has(id))
    const newSelected = new Set(selected)
    if (allSelected) {
      visibleIds.forEach(id => newSelected.delete(id))
    } else {
      visibleIds.forEach(id => newSelected.add(id))
    }
    setSelected(newSelected)
  }

  // Cycle a quick-filter dimension: both (absent) → 'negative' (the problem
  // side, e.g. not-yet-approved) → 'positive' (the inverse, e.g. approved) →
  // both. The first click still lands on the negative side, so the common case
  // works exactly like the old two-state chip.
  const cycleQuickFilter = (key) => {
    setQuickFilterModes(prev => {
      const next = { ...prev }
      const mode = next[key]
      if (mode === undefined) next[key] = 'negative'
      else if (mode === 'negative') next[key] = 'positive'
      else delete next[key]
      return next
    })
  }

  // Admin manual "activate now" — the override for the nightly job. Bypasses the
  // paid AND putaway gates with no warning; never the approval gate (which the
  // server enforces). Lists ONE draft per request so the overlay can show live
  // X/Y progress, and so a big selection never rides a single long Worker
  // request (which used to hang with no feedback). Cancel stops before the next
  // item; anything already activated stays activated.
  const activateProducts = async (targets) => {
    if (targets.length === 0) return
    cancelActivateRef.current = false
    // One run timestamp for the whole bulk action. Each item posts separately
    // (for live progress), but all carry this value so Prism groups them into a
    // single run in the Activation History, not one run per item.
    const runAt = new Date().toISOString()
    const activated = []
    const held = []
    setActivateJob({ total: targets.length, done: 0, current: null, activated, held, phase: 'running', cancelled: false })

    for (let i = 0; i < targets.length; i++) {
      if (cancelActivateRef.current) break
      const t = targets[i]
      setActivateJob(prev => ({ ...prev, current: t }))
      const controller = new AbortController()
      inflightAbortRef.current = controller
      let timedOut = false
      const timer = setTimeout(() => { timedOut = true; controller.abort() }, ACTIVATE_ITEM_TIMEOUT_MS)
      try {
        const result = await api.post(
          '/api/listings/activate',
          { productIds: [String(t.id)], runAt },
          { signal: controller.signal }
        )
        for (const id of (result.activated || [])) activated.push(id)
        for (const h of (result.held || [])) held.push(h)
      } catch (err) {
        // Timeout: record it so a stuck item is visible, not silently missing.
        // User cancel: skip recording — the item's real state shows on refetch.
        // Anything else: a genuine error for this item.
        if (timedOut) {
          held.push({ shopify_product_id: t.id, outcome: 'timed out' })
        } else if (!cancelActivateRef.current) {
          held.push({ shopify_product_id: t.id, outcome: `error: ${err?.message ?? String(err)}` })
        }
      } finally {
        clearTimeout(timer)
        inflightAbortRef.current = null
      }
      setActivateJob(prev => ({ ...prev, done: i + 1, activated: [...activated], held: [...held] }))
    }

    setActivateJob(prev => ({ ...prev, phase: 'done', current: null, cancelled: cancelActivateRef.current }))
  }

  // Cancel the run: stop before the next item AND abort the one in flight, so a
  // hung request can't hold the overlay open with no way out.
  const cancelActivation = () => {
    cancelActivateRef.current = true
    inflightAbortRef.current?.abort()
  }

  // Close the finished overlay: clear the selection and refresh so the
  // just-activated drafts drop off the list.
  const closeActivateJob = () => {
    setActivateJob(null)
    setSelected(new Set())
    fetchDrafts()
  }

  const activateSelected = () => {
    if (selected.size === 0) return

    // Hard blocks first: photos and category must be fixed before listing
    const selectedDrafts = drafts.filter(d => selected.has(d.id))
    const noPhotos = selectedDrafts.filter(d => !d.imageCount || d.imageCount === 0)
    const noType = selectedDrafts.filter(isMissingCategory)

    if (noPhotos.length > 0 || noType.length > 0) {
      const issues = []
      if (noPhotos.length > 0) issues.push(`${noPhotos.length} item(s) have no photos`)
      if (noType.length > 0) issues.push(`${noType.length} item(s) have no product type`)
      alert(`Cannot activate:\n\n${issues.join('\n')}\n\nPlease fix these items before activating.`)
      return
    }

    // No paid/putaway warning: those gates hold the nightly cron, but "Activate
    // now" is a deliberate admin override, so it lists the selection straight
    // through regardless of payment or shelf state. Pass the title too so the
    // progress overlay can name the draft it's currently listing.
    activateProducts(selectedDrafts.map(d => ({ id: d.id, title: d.title })))
  }

  // Approve selected drafts (correctness sign-off). The server enforces
  // no-self-approval; per-item failures (e.g. your own draft) are reported.
  const approveSelected = async () => {
    if (selected.size === 0) return
    setApproving(true)
    let ok = 0
    const failed = []
    for (const id of selected) {
      try {
        await api.post(`/api/listings/${id}/approve`)
        ok++
      } catch (e) {
        failed.push(`• ${id}: ${e.message}`)
      }
    }
    setApproving(false)
    if (failed.length > 0) {
      alert(`Approved ${ok} of ${selected.size}.\n\nCould not approve:\n${failed.join('\n')}`)
    }
    setSelected(new Set())
    fetchDrafts()
  }

  const allFilteredDrafts = useMemo(() => {
    return drafts
      .filter(d => {
        if (filter === 'priced' && (d.sellPrice === 0 || d.isFlagged)) return false
        if (filter === 'unpriced' && d.sellPrice !== 0) return false
        if (filter === 'flagged' && !d.isFlagged) return false
        if (filter === 'nocategory' && !isMissingCategory(d)) return false
        if (filter === 'nocost' && !hasMissingCost(d)) return false
        if (filter === 'held' && !d.heldReason) return false
        if (filter === 'notputaway' && d.locationLabel) return false
        // Quick-filter chips (intersection / AND): each active dimension holds a
        // mode — 'negative' keeps drafts IN its bucket (the problem side),
        // 'positive' keeps drafts OUT of it (the resolved side). A dimension in
        // the resting 'both' state isn't in the map, so it adds no constraint.
        // Same buckets the chip counts use, so the two can never drift. ANDs with
        // the Status dropdown and search too.
        for (const [key, mode] of Object.entries(quickFilterModes)) {
          const inBucket = QUICK_FILTER_BUCKETS[key](d)
          if (mode === 'negative' && !inBucket) return false
          if (mode === 'positive' && inBucket) return false
        }
        if (searchTerm) {
          const term = searchTerm.toLowerCase()
          return d.title?.toLowerCase().includes(term) || d.product_type?.toLowerCase().includes(term)
        }
        return true
      })
      .sort((a, b) => {
        // Missing-cost rows always float to the top, whatever the chosen sort
        const aMissing = hasMissingCost(a) ? 0 : 1
        const bMissing = hasMissingCost(b) ? 0 : 1
        if (aMissing !== bMissing) return aMissing - bMissing
        let aVal, bVal
        if (sortBy === 'price') { aVal = a.sellPrice; bVal = b.sellPrice }
        else if (sortBy === 'cost') { aVal = a.costPrice; bVal = b.costPrice }
        else if (sortBy === 'type') { aVal = a.product_type || ''; bVal = b.product_type || '' }
        else if (sortBy === 'photos') { aVal = a.imageCount || 0; bVal = b.imageCount || 0 }
        else { aVal = a.title || ''; bVal = b.title || '' }
        if (typeof aVal === 'string') return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
        return sortDir === 'asc' ? aVal - bVal : bVal - aVal
      })
  }, [drafts, filter, quickFilterModes, searchTerm, sortBy, sortDir])

  const filteredDrafts = useMemo(() => {
    return allFilteredDrafts.slice(0, limit)
  }, [allFilteredDrafts, limit])

  const stats = {
    total: drafts.length,
    priced: drafts.filter(d => d.sellPrice > 0.01).length,
    unpriced: drafts.filter(d => d.sellPrice === 0).length,
    flagged: drafts.filter(d => d.isFlagged).length,
    noCategory: drafts.filter(isMissingCategory).length,
    noCost: drafts.filter(hasMissingCost).length,
    held: drafts.filter(d => d.heldReason).length,
    // Gate-state tiles — match the per-row badges: Approved = signed off;
    // Unpaid = not consignment and not paid (a missing overlay reads as unpaid,
    // exactly as the row badge does).
    approved: drafts.filter(d => d.approvalStatus === 'approved').length,
    unpaid: drafts.filter(d => !d.gateConsignment && !d.paid).length,
    // Not yet putaway = no committed shelf. locationLabel is item_location.shelf_id
    // (null until the item is put away), so this mirrors the nightly putaway gate.
    notPutaway: drafts.filter(d => !d.locationLabel).length,
    totalValue: drafts.reduce((sum, d) => sum + (d.sellPrice > 0.01 ? d.sellPrice : 0), 0),
    totalCost: drafts.reduce((sum, d) => sum + (d.costPrice || 0), 0)
  }

  // A draft matches a dimension's mode: 'positive' keeps it OUT of the bucket
  // (the resolved side), otherwise IN it. Shared by the count below and mirrors
  // the row filter's negative/positive test, so counts and filter can't drift.
  const matchesQuickMode = (d, key, mode) => {
    const inBucket = QUICK_FILTER_BUCKETS[key](d)
    return mode === 'positive' ? !inBucket : inBucket
  }

  // Contextual chip counts (see the chip row below): for each dimension, how
  // many drafts match its CURRENT mode AND every OTHER active dimension's mode.
  // So an active chip shows its narrowed subset, and the number updates as you
  // cycle the others. A resting ('both') chip has no count rendered; we preview
  // its negative side here so the value is ready the moment it activates.
  // Reflects chip selections only — independent of the Search box + Status
  // dropdown — so the numbers stay put as you type or pick. The KPI tiles above
  // keep their own standalone totals via `stats`.
  const quickFilterCounts = Object.fromEntries(
    Object.keys(QUICK_FILTER_BUCKETS).map((key) => [
      key,
      drafts.filter(
        (d) =>
          matchesQuickMode(d, key, quickFilterModes[key] || 'negative') &&
          Object.entries(quickFilterModes).every(
            ([active, mode]) => active === key || matchesQuickMode(d, active, mode)
          )
      ).length,
    ])
  )

  // One 3-way cycling chip. Resting ('both') is a neutral secondary button with
  // no count; 'negative' is the familiar blue "on" look, 'positive' is green so
  // an inverted filter reads at a glance. Count comes from quickFilterCounts.
  const renderQuickFilter = (key) => {
    const mode = quickFilterModes[key]
    const labels = QUICK_FILTER_LABELS[key]
    const variant = mode === 'negative' ? 'btn-primary'
      : mode === 'positive' ? 'btn-success'
      : 'btn-secondary'
    return (
      <button
        key={key}
        type="button"
        className={`btn btn-sm ${variant}`}
        onClick={() => cycleQuickFilter(key)}
      >
        {mode ? `✓ ${labels[mode]} (${quickFilterCounts[key]})` : labels.both}
      </button>
    )
  }

  const getRowClass = (draft) => {
    const classes = []
    if (selected.has(draft.id)) classes.push('row-selected')
    if (pendingChanges[draft.id]) classes.push('row-modified')
    return classes.join(' ')
  }

  async function handleRowClick(productId) {
    setLoadingProduct(true)
    try {
      const response = await api.get(`/api/inventory/shopify/product/${productId}`)
      // Merge the row-decorated locationLabel onto the Shopify product
      // response so ProductDetailModal can skip its lazy-fetch fallback.
      // Without this the modal sees warehouseLocation=null and re-fetches
      // location data we already pulled in fetchDrafts.
      const draft = drafts.find(d => String(d.id) === String(productId))
      setSelectedProduct({ ...response.product, locationLabel: draft?.locationLabel || null })
    } catch (err) {
      console.error('Failed to load product:', err)
    } finally {
      setLoadingProduct(false)
    }
  }

  async function handleProductSave(productId, updates) {
    try {
      // Price updates go via the POST endpoint (handles variant pricing).
      // D15: a below-floor consignment price warns-and-confirms, never blocks.
      if (updates.price !== undefined) {
        await postPriceWithFloorConfirm(`/api/inventory/shopify/update/${productId}`, { price: updates.price })
      }
      // Remaining fields (description, title, vendor, etc.) via PUT.
      const { price, ...otherUpdates } = updates
      if (Object.keys(otherUpdates).length > 0) {
        await api.put(`/api/inventory/shopify/product/${productId}`, otherUpdates)
      }
      // Re-fetch the saved product so the open modal reflects persisted
      // values, preserving the row-decorated locationLabel like handleRowClick.
      const response = await api.get(`/api/inventory/shopify/product/${productId}`)
      const draft = drafts.find(d => String(d.id) === String(productId))
      setSelectedProduct({ ...response.product, locationLabel: draft?.locationLabel || null })
      await fetchDrafts()
    } catch (err) {
      console.error('Failed to save product:', err)
      throw err
    }
  }

  async function handleProductDelete(productId) {
    try {
      await api.delete(`/api/inventory/${productId}`)
      setSelectedProduct(null)
      await fetchDrafts()
    } catch (err) {
      console.error('Failed to delete product:', err)
      throw err
    }
  }

  const getPriceClass = (draft) => {
    if (draft.isFlagged) return 'text-warning'
    if (draft.isUnpriced) return 'text-error'
    return 'text-success'
  }

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(field)
      setSortDir('asc')
    }
  }

  const sortIndicator = (field) => sortBy === field ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''

  const pendingCount = Object.keys(pendingChanges).length
  // Derived from the job so the button disables while the overlay runs.
  const activating = activateJob?.phase === 'running'

  // Bulk print in table order (the full filtered+sorted list, not just the
  // visible page, so a selection spanning pages still prints top-to-bottom).
  const selectedPrintableDrafts = allFilteredDrafts.filter(d => selected.has(d.id) && canPrintLabel(d))

  function printSelectedLabels() {
    printSelected(selectedPrintableDrafts)
  }

  const metrics = [
    { value: stats.total, label: 'Total Drafts', color: '' },
    { value: stats.priced, label: 'Priced', color: 'green' },
    { value: stats.unpriced, label: 'Unpriced', color: 'red' },
    { value: stats.approved, label: 'Approved', color: 'green' },
    { value: stats.unpaid, label: 'Unpaid', color: 'amber' },
    { value: stats.notPutaway, label: 'Not yet putaway', color: 'amber' },
    ...(stats.noCategory > 0 ? [{ value: stats.noCategory, label: 'No Category', color: 'purple' }] : []),
    ...(stats.noCost > 0 ? [{ value: stats.noCost, label: 'No Cost', color: 'red' }] : []),
    { value: stats.totalValue, label: 'Total Value', color: 'green', isCurrency: true },
    { value: stats.totalCost, label: 'Total Cost', color: 'muted', isCurrency: true }
  ]

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 'var(--space-12)' }}>
        <div className="loading-spinner" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="card" style={{ textAlign: 'center', marginTop: 'var(--space-8)' }}>
        <div style={{ fontSize: '48px', marginBottom: 'var(--space-4)' }}>⚠</div>
        <p className="text-muted" style={{ marginBottom: 'var(--space-4)' }}>{error}</p>
        <button onClick={fetchDrafts} className="btn btn-primary">Retry</button>
      </div>
    )
  }

  return (
    <div>
      {/* No-cost gate retired (spec 20260825): the held model gates activation,
          not a cost check. The "No Cost" row badge remains as a visual cue. */}

      {/* Filter Panel */}
      <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
        <div className="filter-section">
          <div className="filter-group filter-group-wide">
            <label className="filter-label">Search</label>
            <input
              type="text"
              placeholder="Search by title or category..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-input"
            />
          </div>
          <div className="filter-group">
            <label className="filter-label">Status</label>
            <select value={filter} onChange={(e) => setFilter(e.target.value)} className="form-input">
              <option value="all">All ({stats.total})</option>
              <option value="priced">Priced ({stats.priced})</option>
              <option value="unpriced">Unpriced ({stats.unpriced})</option>
              <option value="flagged">Flagged ({stats.flagged})</option>
              <option value="nocategory">No Category ({stats.noCategory})</option>
              <option value="nocost">No Cost ({stats.noCost})</option>
              <option value="held">Held ({stats.held})</option>
              <option value="notputaway">Not putaway ({stats.notPutaway})</option>
            </select>
          </div>
        </div>

        {/* Actions */}
        <div className="filter-actions">
          <span className="text-sm text-muted">
            {drafts.length > 0 && `${filteredDrafts.length} of ${drafts.length} draft products`}
          </span>
          <div className="filter-actions-right">
            <button onClick={fetchDrafts} className="btn btn-secondary">
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Metrics Row */}
      <MetricsRow metrics={metrics} />

      {/* Quick filters — each a 3-way cycle (both → problem → resolved),
          intersected (AND) with the others + search + dropdown */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap', marginBottom: 'var(--space-4)' }}>
        <span className="text-sm text-muted">Quick filters:</span>
        {Object.keys(QUICK_FILTER_LABELS).map(renderQuickFilter)}
        {Object.keys(quickFilterModes).length > 0 && (
          <button type="button" className="btn btn-sm btn-secondary" onClick={() => setQuickFilterModes({})}>
            Clear
          </button>
        )}
      </div>

      {/* Bulk Actions */}
      {filteredDrafts.length > 0 && (
        <div className="card" style={{ marginBottom: 'var(--space-4)', padding: 'var(--space-3)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
            <span className="text-sm" style={{ fontWeight: 500, color: 'var(--color-primary)' }}>
              {selected.size} selected
            </span>

            {pendingCount > 0 && (
              <button onClick={saveChanges} disabled={saving} className="btn btn-primary">
                {saving ? 'Saving...' : `Save Changes (${pendingCount})`}
              </button>
            )}

            {canApprove && (
              <button
                onClick={approveSelected}
                disabled={selected.size === 0 || approving}
                className="btn btn-primary"
              >
                {approving ? 'Approving…' : 'Approve Selected'}
              </button>
            )}

            {user?.is_admin && (
              <OffOnStaging feature="listing_activation">
              <button
                onClick={activateSelected}
                disabled={selected.size === 0 || activating}
                className="btn btn-success"
                title="Admin override: list now instead of waiting for the 18:00 job. The paid gate can be overridden here; approval cannot."
              >
                {activating ? 'Activating...' : 'Activate now'}
              </button>
              </OffOnStaging>
            )}
            <button
              onClick={printSelectedLabels}
              disabled={selectedPrintableDrafts.length === 0 || bulkPrinting}
              className="btn btn-secondary"
              title={
                selected.size > 0 && selectedPrintableDrafts.length === 0
                  ? 'Selected drafts need a title and a price before printing'
                  : undefined
              }
            >
              {bulkPrinting ? 'Printing labels…' : `Print Selected Labels${selectedPrintableDrafts.length > 0 ? ` (${selectedPrintableDrafts.length})` : ''}`}
            </button>
          </div>
        </div>
      )}

      <div className="card">
        {filteredDrafts.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-title">No Draft Products</div>
            <div className="empty-state-description">
              {drafts.length === 0
                ? 'No draft products found in Shopify.'
                : 'Try adjusting your filters to see more products.'}
            </div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 40 }}>
                  <input
                    type="checkbox"
                    checked={filteredDrafts.length > 0 && filteredDrafts.every(d => selected.has(d.id))}
                    onChange={selectAllVisible}
                  />
                </th>
                <th style={{ width: 60 }}>Image</th>
                <th style={{ width: 50, textAlign: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('photos')}>Photos{sortIndicator('photos')}</th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('title')}>Title{sortIndicator('title')}</th>
                <th style={{ width: 180, cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('type')}>Category{sortIndicator('type')}</th>
                <th style={{ width: 100, textAlign: 'right', cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('cost')}>Cost{sortIndicator('cost')}</th>
                <th style={{ width: 100, textAlign: 'right', cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('price')} title="Anchor — the price you set; channel prices derive from it">Anchor{sortIndicator('price')}</th>
                <th style={{ width: 150, textAlign: 'right' }} title="Launch list price per channel, derived from the anchor (read-only)">Pricing</th>
                <th style={{ width: 100, textAlign: 'center' }}>Status</th>
                <th style={{ width: 120 }}>Search</th>
                <th style={{ width: 110, textAlign: 'center' }}>Print Label</th>
              </tr>
            </thead>
            <tbody>
              {filteredDrafts.map((draft) => (
                <tr
                  key={draft.id}
                  className={getRowClass(draft)}
                  onClick={() => handleRowClick(draft.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <td onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selected.has(draft.id)}
                      onChange={() => toggleSelect(draft.id)}
                    />
                  </td>
                  <td>
                    {draft.image ? (
                      <img
                        src={draft.image}
                        alt=""
                        style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 'var(--radius-sm)' }}
                      />
                    ) : (
                      <div style={{ width: 48, height: 48, background: 'var(--color-gray-100)', borderRadius: 'var(--radius-sm)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-gray-400)', fontSize: 11 }}>
                        No img
                      </div>
                    )}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <span className={draft.imageCount === 0 ? 'text-error' : draft.imageCount < 3 ? 'text-warning' : 'text-success'}>
                      {draft.imageCount}
                    </span>
                  </td>
                  <td>
                    <div style={{ fontWeight: 500, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <EditableCell
                        value={draft.title}
                        onChange={(newValue) => updateDraft(draft.id, 'title', newValue)}
                      />
                    </div>
                    <div className="text-sm text-muted" style={{ fontFamily: 'monospace', fontSize: 10 }}>
                      {draft.locationLabel || '—'}
                      {/* Ownership badge, tag-driven (spec D7/D12). It explains the
                          legitimately empty cost — the D11 no-cost Activate gate
                          still fires on consignment drafts like any other. */}
                      {Array.isArray(draft.tags) && draft.tags.includes('consignment') && (
                        <span className="badge badge-purple" style={{ marginLeft: 6 }} title="Consignment — we don't own this item; cost is legitimately empty">
                          Consignment
                        </span>
                      )}
                    </div>
                    {draft.quoteReference && (
                      <div className="text-sm text-muted" style={{ fontFamily: 'monospace', fontSize: 10 }}>
                        {draft.quoteReference}
                        {draft.quoteStage && (
                          <span className={`badge ${QUOTE_STAGE_BADGE[draft.quoteStage] || 'badge-neutral'}`} style={{ marginLeft: 6 }}>
                            {draft.quoteStageLabel || draft.quoteStage}
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                  <td onClick={e => e.stopPropagation()}>
                    <CategoryDropdown
                      value={draft.product_type}
                      onChange={(newValue) => updateDraft(draft.id, 'product_type', newValue)}
                      hasError={isMissingCategory(draft)}
                    />
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <EditablePriceCell
                      value={draft.costPrice}
                      onChange={(newValue) => updateDraft(draft.id, 'cost', newValue)}
                      className={hasMissingCost(draft) ? 'text-error' : ''}
                    />
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <EditablePriceCell
                      value={draft.sellPrice}
                      onChange={(newValue) => updateDraft(draft.id, 'price', newValue)}
                      className={getPriceClass(draft)}
                    />
                    {draft.trialPrice && (
                      <span className="badge badge-info" style={{ marginLeft: 6 }} title="Price set by the SP trial. Change it only if it is clearly wrong.">
                        Trial
                      </span>
                    )}
                    {(() => {
                      // qty>1: show the per-unit × qty = total so the approver
                      // confirms this is a per-unit price, not a whole-lot total
                      // (CONTEXT.md "Line price (per-unit) & Quantity").
                      const note = perUnitLineTotal(draft)
                      return note && (
                        <div
                          style={{ fontSize: 11, color: '#b45309', marginTop: 2, whiteSpace: 'nowrap' }}
                          title="This is the price PER UNIT — the draft lists at this price × the quantity in stock. Confirm it isn't the whole-lot total before approving; if it's a bundle, set quantity to 1."
                        >
                          ⚠ {formatCurrency(note.unit)} × {note.qty} = {formatCurrency(note.total)}
                        </div>
                      )
                    })()}
                  </td>
                  {(() => {
                    const anchorCents = Math.round((draft.sellPrice || 0) * 100)
                    const pv = previewMap[anchorCents]
                    const cell = (cents) =>
                      cents != null ? formatCurrency(cents / 100) : <span style={{ color: '#6b7280' }}>&mdash;</span>
                    const line = (color, label, cents) => (
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-3)' }}>
                        <span style={{ color: '#a0a0c0' }}><span style={{ color }}>● </span>{label}</span>
                        <span style={{ color: '#a0a0c0' }}>{cell(cents)}</span>
                      </div>
                    )
                    // A consignment draft lists at its agreed price on day 1 (curve
                    // month 1) on every channel, then glides to its floor once live
                    // (spec_20260918). Pre-listing we show that day-1 price — the
                    // anchor — not the +5% launch markup the /preview endpoint returns.
                    const consignment = draft.isConsignment
                    const anchorOrNull = anchorCents > 0 ? anchorCents : null
                    return (
                      <td style={{ fontSize: 12, lineHeight: 1.5, whiteSpace: 'nowrap' }}>
                        {line('#96BF48', 'D2C', consignment ? anchorOrNull : pv?.shopify)}
                        {line('#E4002B', 'Reverb', consignment ? anchorOrNull : pv?.reverb)}
                        {line('#0064D2', 'eBay', consignment ? anchorOrNull : pv?.ebay)}
                        {consignment && (
                          <div style={{ fontSize: 10, color: '#b45309' }}>consignment · curve</div>
                        )}
                      </td>
                    )
                  })()}
                  <td style={{ textAlign: 'center' }}>
                    {hasMissingCost(draft) && <StatusBadge status="No Cost" type="error" />}
                    <DraftStatusBadge draft={draft} />
                    {draft.approvalStatus === 'approved'
                      ? <StatusBadge status="Approved" type="success" />
                      : <StatusBadge status="Needs approval" type="warning" />}
                    {draft.gateConsignment
                      ? <StatusBadge status="Consignment" type="purple" />
                      : draft.paid
                        ? <StatusBadge status="Paid" type="success" />
                        : <StatusBadge status="Unpaid" type="warning" />}
                  </td>
                  <td onClick={e => e.stopPropagation()}>
                    <SearchLinks title={draft.title} />
                    {hasPermission('marketing') && (
                      <div style={{ marginTop: 6 }}>
                        <StaffPickButton
                          productId={draft.id}
                          title={draft.title}
                          handle={draft.handle}
                          priceCents={draft.sellPrice ? Math.round(parseFloat(draft.sellPrice) * 100) : null}
                          imageUrl={draft.image}
                        />
                      </div>
                    )}
                  </td>
                  <td style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                    <button
                      type="button"
                      className={`btn btn-sm ${rowState(draft.id) === 'failed' ? 'btn-danger' : 'btn-secondary'}`}
                      disabled={!canPrintLabel(draft) || rowState(draft.id) === 'printing' || bulkPrinting}
                      title={!canPrintLabel(draft) ? 'Needs a title and a price before a label can be printed' : 'Print a 4x6 shelf label'}
                      onClick={() => printOne(draft)}
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      {PRINT_BUTTON_TEXT[rowState(draft.id)]}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}

        {/* Per-page selector */}
        {filteredDrafts.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 'var(--space-3)', borderTop: '1px solid var(--color-border)' }}>
            <span className="text-sm text-muted">
              Showing {filteredDrafts.length} of {allFilteredDrafts.length} filtered ({drafts.length} total)
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              <span className="text-sm text-muted">Per page:</span>
              {[25, 50, 100, 200].map(num => (
                <button
                  key={num}
                  type="button"
                  className={`btn btn-sm ${limit === num ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setLimit(num)}
                >
                  {num}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {fallback && (
        <PrintFallbackDialog
          zpl={fallback.zpl}
          filename={fallback.filename}
          onClose={clearFallback}
        />
      )}

      {/* Activation progress overlay. No backdrop-dismiss while running, so a
          click can't abandon a run halfway — Cancel is the only way out. */}
      {activateJob && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: 520 }}>
            <div className="modal-header">
              <h2>{activateJob.phase === 'done' ? 'Activation complete' : 'Activating drafts'}</h2>
              {activateJob.phase === 'done' && (
                <button className="modal-close" onClick={closeActivateJob}>&times;</button>
              )}
            </div>
            <div style={{ padding: 'var(--space-4)' }}>
              {activateJob.phase === 'running' ? (
                <>
                  <ProgressBar
                    current={activateJob.done}
                    total={activateJob.total}
                    status={activateJob.current ? `Activating: ${activateJob.current.title}` : 'Starting…'}
                    onCancel={cancelActivation}
                  />
                  <p className="text-sm text-muted" style={{ marginTop: 'var(--space-3)' }}>
                    Listing one at a time. Keep this open — Cancel stops before the next item;
                    anything already listed stays live.
                  </p>
                </>
              ) : (
                <>
                  <p style={{ fontWeight: 600, marginBottom: 'var(--space-2)' }}>
                    Activated {activateJob.activated.length} of {activateJob.total}
                    {activateJob.cancelled ? ' (cancelled)' : ''}.
                  </p>
                  {activateJob.held.length > 0 && (
                    <div style={{ marginBottom: 'var(--space-3)' }}>
                      <p className="text-sm" style={{ fontWeight: 600, marginBottom: 'var(--space-1)' }}>
                        Held ({activateJob.held.length}):
                      </p>
                      <ul className="text-muted" style={{ margin: 0, paddingLeft: 'var(--space-4)', fontSize: 'var(--font-size-sm)' }}>
                        {activateJob.held.map((h, idx) => (
                          <li key={idx}>{h.shopify_product_id} — {h.outcome}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <button className="btn btn-primary" onClick={closeActivateJob}>Close</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {selectedProduct && (
        <ProductDetailModal
          product={selectedProduct}
          warehouseLocation={selectedProduct.locationLabel || null}
          canEditPrice={true}
          canEditType={false}
          canEditTags={false}
          showMarketPrice={hasPermission('pricing:view')}
          onClose={() => setSelectedProduct(null)}
          onSave={handleProductSave}
          onDelete={user?.is_admin ? handleProductDelete : undefined}
        />
      )}
    </div>
  )
}

export default DraftsManager
